<template>
  <transition name="el-zoom-in-top" @after-enter="handleEnter" @after-leave="handleLeave">
    <div
      v-show="visible"
      class="el-picker-panel el-date-picker el-popper"
      :class="[
        {
          'has-sidebar': $slots.sidebar || shortcuts,
          'has-time': showTime,
        },
        popperClass,
      ]"
    >
      <el-tabs v-model="tabActive" class="qm-picker-tabs">
        <el-tab-pane label="按日" name="date">
          <SwitchDateMonthDatePanel ref="datePanel" @pick="onDatePicker" />
        </el-tab-pane>
        <el-tab-pane label="按月" name="month">
          <SwitchDateMonthDatePanel ref="monthPanel" @pick="onMonthPicker" />
        </el-tab-pane>
      </el-tabs>
    </div>
  </transition>
</template>

<script type="text/babel">
import {
  formatDate,
  getWeekNumber,
  clearMilliseconds,
  clearTime,
  extractDateFormat,
  extractTimeFormat,
} from 'qmai-ui/src/utils/date-util'
import Clickoutside from 'qmai-ui/src/utils/clickoutside'
import Locale from 'qmai-ui/src/mixins/locale'
import ElInput from 'qmai-ui/packages/input'
import ElButton from 'qmai-ui/packages/button'
import TimePicker from './time'
import YearTable from '../basic/year-table'
import MonthTable from '../basic/month-table'
import DateTable from '../basic/date-table'
import SwitchDateMonthDatePanel from '../panel/switch-date-month-date.vue'

export default {
  mixins: [Locale],

  directives: { Clickoutside },

  watch: {
    showTime(val) {
      this.$refs.datePanel.showTime = val
      this.$refs.monthPanel.showTime = val
    },

    defaultTime(val) {
      this.$refs.datePanel.defaultTime = val
      this.$refs.monthPanel.defaultTime = val
    },

    value(val) {
      this.$refs.datePanel.value = val
      this.$refs.monthPanel.value = val
    },

    defaultValue(val) {
      this.$refs.datePanel.defaultValue = val
      this.$refs.monthPanel.defaultValue = val
    },

    timePickerVisible(val) {
      if (val) this.$nextTick(() => this.$refs.timepicker.adjustSpinners())
    },

    selectionMode(val) {
      this.$refs.datePanel.selectionMode = val
      this.$refs.monthPanel.selectionMode = val
    },

    disabledDate(val) {
      if (typeof val === 'object') {
        val.date && (this.$refs.datePanel.disabledDate = val.date)
        val.month && (this.$refs.monthPanel.disabledDate = val.month)
      } else {
        this.$refs.datePanel.disabledDate = val
        this.$refs.monthPanel.disabledDate = val
      }
    },
  },

  methods: {
    onDatePicker(value, ...args) {
      this.emit(value, ...args)
    },

    onMonthPicker(value, ...args) {
      if (!value) {
        this.$emit('pick', value, ...args)
      } else {
        // 特殊处理，需要在日期字符串中能够标识是按月选择的
        this.$emit('pick', new Date(formatDate(value, 'yyyy-MM-dd 23:59:59')), ...args)
      }
    },

    emit(value, ...args) {
      if (!value) {
        this.$emit('pick', value, ...args)
      } else if (Array.isArray(value)) {
        const dates = value.map(date => (this.showTime ? clearMilliseconds(date) : clearTime(date)))
        this.$emit('pick', dates, ...args)
      } else {
        this.$emit('pick', this.showTime ? clearMilliseconds(value) : clearTime(value), ...args)
      }
      this.userInputDate = null
      this.userInputTime = null
    },

    handleEnter() {
      document.body.addEventListener('keydown', this.handleKeydown)
    },

    handleLeave() {
      this.$emit('dodestroy')
      document.body.removeEventListener('keydown', this.handleKeydown)
    },

    handleKeydown(event) {
      const keyCode = event.keyCode
      const list = [38, 40, 37, 39]
      if (this.visible && !this.timePickerVisible) {
        if (list.indexOf(keyCode) !== -1) {
          this.handleKeyControl(keyCode)
          event.stopPropagation()
          event.preventDefault()
        }
        if (keyCode === 13 && this.userInputDate === null && this.userInputTime === null) {
          // Enter
          this.emit(this.date, false)
        }
      }
    },

    handleKeyControl(keyCode) {
      const mapping = {
        year: {
          38: -4,
          40: 4,
          37: -1,
          39: 1,
          offset: (date, step) => date.setFullYear(date.getFullYear() + step),
        },
        month: {
          38: -4,
          40: 4,
          37: -1,
          39: 1,
          offset: (date, step) => date.setMonth(date.getMonth() + step),
        },
        week: {
          38: -1,
          40: 1,
          37: -1,
          39: 1,
          offset: (date, step) => date.setDate(date.getDate() + step * 7),
        },
        day: {
          38: -7,
          40: 7,
          37: -1,
          39: 1,
          offset: (date, step) => date.setDate(date.getDate() + step),
        },
      }
      const mode = this.selectionMode
      const year = 3.1536e10
      const now = this.date.getTime()
      const newDate = new Date(this.date.getTime())
      while (Math.abs(now - newDate.getTime()) <= year) {
        const map = mapping[mode]
        map.offset(newDate, map[keyCode])
        if (typeof this.disabledDate === 'function' && this.disabledDate(newDate)) {
          continue
        }
        this.date = newDate
        this.$emit('pick', newDate, true)
        break
      }
    },

    getDefaultValue() {
      // if default-value is set, return it
      // otherwise, return now (the moment this method gets called)
      return this.defaultValue ? new Date(this.defaultValue) : new Date()
    },

    handleClear() {
      this.$refs.datePanel.handleClear()
      this.$refs.monthPanel.handleClear()
    },

    resetView() {
      this.$refs.datePanel.resetView()
      this.$refs.monthPanel.resetView()
    },

    isValidValue() {
      this.$refs.datePanel.isValidValue()
      this.$refs.monthPanel.isValidValue()
    },
  },

  components: {
    TimePicker,
    YearTable,
    MonthTable,
    DateTable,
    ElInput,
    ElButton,
    SwitchDateMonthDatePanel,
  },

  data() {
    return {
      popperClass: '',
      date: new Date(),
      value: '',
      defaultValue: null, // use getDefaultValue() for time computation
      defaultTime: null,
      showTime: false,
      selectionMode: 'day',
      shortcuts: '',
      visible: false,
      currentView: 'date',
      disabledDate: '',
      cellClassName: '',
      selectableRange: [],
      firstDayOfWeek: 7,
      showWeekNumber: false,
      timePickerVisible: false,
      format: '',
      arrowControl: false,
      userInputDate: null,
      userInputTime: null,
      tabActive: 'date',
    }
  },

  computed: {
    year() {
      return this.date.getFullYear()
    },

    month() {
      return this.date.getMonth()
    },

    week() {
      return getWeekNumber(this.date)
    },

    monthDate() {
      return this.date.getDate()
    },

    footerVisible() {
      return this.showTime || this.selectionMode === 'dates'
    },

    visibleTime() {
      if (this.userInputTime !== null) {
        return this.userInputTime
      } else {
        return formatDate(this.value || this.defaultValue, this.timeFormat)
      }
    },

    visibleDate() {
      if (this.userInputDate !== null) {
        return this.userInputDate
      } else {
        return formatDate(this.value || this.defaultValue, this.dateFormat)
      }
    },

    yearLabel() {
      const yearTranslation = this.t('el.datepicker.year')
      if (this.currentView === 'year') {
        const startYear = Math.floor(this.year / 10) * 10
        if (yearTranslation) {
          return startYear + ' ' + yearTranslation + ' - ' + (startYear + 9) + ' ' + yearTranslation
        }
        return startYear + ' - ' + (startYear + 9)
      }
      return this.year + ' ' + yearTranslation
    },

    timeFormat() {
      if (this.format) {
        return extractTimeFormat(this.format)
      } else {
        return 'HH:mm:ss'
      }
    },

    dateFormat() {
      if (this.format) {
        return extractDateFormat(this.format)
      } else {
        return 'yyyy-MM-dd'
      }
    },
  },

  mounted() {
    this.$refs.monthPanel.selectionMode = 'month'
    this.$refs.monthPanel.resetView()
  },
}
</script>
